import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsAppsService } from './projects-apps.service';
import { PJTokenResponse } from '../../models/pjtoken-response';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, catchError, EMPTY, Observable, of, ReplaySubject, switchMap, tap, throwError } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { AuthRoleService } from './auth-role.service';

@Injectable({
  providedIn: 'root'
})
export class AppInitService {


  private initialized = false;
  private readonly projectNameSubject = new BehaviorSubject<string>('');
  projectName$ = this.projectNameSubject.asObservable();

  private readonly tokensReadySubject = new ReplaySubject<boolean>(1);
  public tokensReady$ = this.tokensReadySubject.asObservable();

  private readonly hasAccessSubject = new BehaviorSubject<boolean | null>(null);
  hasAccess$ = this.hasAccessSubject.asObservable();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly projectsAppsService: ProjectsAppsService,
    private readonly titleService: Title,
    private readonly router: Router,
    private readonly authRoleService: AuthRoleService,
  ) { }

  public init(url?: string): void {
    if (this.initialized) return;
    this.initialized = true;

    // Parse window.location.search synchronously because route.queryParams can
    // emit an empty object on the first load before the Angular router resolves
    // the URL.
    const searchSource = url
      ? (url.includes('?') ? url.split('?')[1] : url)
      : window.location.search.substring(1);
    const windowParams = new URLSearchParams(searchSource);
    const snapshotParams = this.route.snapshot.queryParams;

    const token: string | null = snapshotParams['token'] || windowParams.get('token') || null;
    const projectId: string | null = snapshotParams['projectId'] || windowParams.get('projectId') || null;
    const guid: string | null = snapshotParams['guid'] || windowParams.get('guid') || null;
    const appId: string | null = snapshotParams['appsId'] || windowParams.get('appsId') || null;

    const storedToken = sessionStorage.getItem('msal_access_token');
    if (!storedToken && !token) {
      this.hasAccessSubject.next(false);
      this.tokensReadySubject.next(true);
      return;
    }

    const needsProjectToken = !!token || !sessionStorage.getItem('access_token');
    if (storedToken && !needsProjectToken) {
      this.tokensReadySubject.next(true);
    }

    if (token) {
      sessionStorage.setItem('msal_access_token', token);
      sessionStorage.removeItem('access_token');
    }

    this.hasAccessSubject.next(true);

    if (projectId) {
      sessionStorage.setItem('projectId', projectId);
      console.log('External projectId recibido:', projectId);
    }
    if (guid) {
      sessionStorage.setItem('guid', guid);
      console.log('External guid recibido:', guid);
    }
    if (appId) {
      sessionStorage.setItem('appId', appId);
      console.log('External appId recibido:', appId);
    }

    this.projectsAppsService.getAppSessions(guid ?? sessionStorage.getItem('guid') ?? '').pipe(
      tap(response => {
        sessionStorage.setItem('account', response.session);
        const account = JSON.parse(decodeURIComponent(response.session));
        sessionStorage.setItem('username', account.username);
        const sessionProjectId: string | null = account.projectId || null;
        if (sessionProjectId) {
          sessionStorage.setItem('projectId', sessionProjectId);
          console.log('Session projectId recibido:', sessionProjectId);
        }
      }),
      switchMap(() => needsProjectToken ? this.postProjectsToken$() : of(null)),
      tap(() => this.tokensReadySubject.next(true)),
      catchError((err: HttpErrorResponse) => {
        console.error('Error Projects Apps:', err);
        this.tokensReadySubject.next(false);
        return EMPTY;
      })
    ).subscribe();
  }

  public setTokenStatus(status: boolean): void {
    this.tokensReadySubject.next(status);
  }

  postProjectsToken$(): Observable<PJTokenResponse> {
    const token = sessionStorage.getItem('msal_access_token');
    if (!token) {
      return throwError(() => new Error('No Microsoft access token available.'));
    }
    const projectId = sessionStorage.getItem('projectId')!;
    const appId = sessionStorage.getItem('appId')!;

    return this.projectsAppsService.getProjIOTestToken(projectId, appId, token).pipe(
      tap(res => {
        sessionStorage.setItem('access_token', res.access_token);
        sessionStorage.setItem('project', res.project);
        sessionStorage.setItem('projectname', res.projectname);
        sessionStorage.setItem('roles', res.roles);
        sessionStorage.setItem('customer', res.customer);
        sessionStorage.setItem('customername', res.customername);
        sessionStorage.setItem('factory', res.factory);
        sessionStorage.setItem('factoryname', res.factoryname);
        sessionStorage.setItem('iotestorganization', res.iotestorganization);
        sessionStorage.setItem('iotestorganizationname', res.iotestorganizationname);
        sessionStorage.setItem('iotestproject', res.iotestproject);
        sessionStorage.setItem('iotestprojectname', res.iotestprojectname);
        this.authRoleService.reloadRoles();
        this.titleService.setTitle(`IO Test ${res.customername} / ${res.factoryname} / ${res.projectname}  ${res.iotestorganizationname}`);
        this.updateProjectName(res.customername, res.factoryname, res.projectname);
      }),
      catchError(err => {
        console.error('Error Projects Token:', err);
        return throwError(() => err);
      })
    );
  }

  updateProjectName(customername: string = '', factoryname: string = '', projectname: string = ''): void {
    const name = `${customername} - ${factoryname} - ${projectname}`;
    this.projectNameSubject.next(name);
  }

}
